/**
 * Calm-tech anomaly detection — z-score over per-category rolling baseline.
 * Triggers only when |z| > 2.5 and amount > absolute floor.
 */

export interface AnomalyPoint {
  readonly category: string;
  readonly amount: number;
  readonly date: string;
}

export interface AnomalyResult {
  readonly category: string;
  readonly amount: number;
  readonly z: number;
  readonly baselineMean: number;
  readonly baselineStd: number;
  readonly verdict: 'normal' | 'high' | 'low';
}

const FLOOR_INR = 500;

function meanStd(xs: number[]): { mean: number; std: number } {
  if (xs.length === 0) return { mean: 0, std: 0 };
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  if (xs.length === 1) return { mean, std: 0 };
  const variance = xs.reduce((a, b) => a + (b - mean) ** 2, 0) / (xs.length - 1);
  return { mean, std: Math.sqrt(variance) };
}

export function detectAnomalies(history: AnomalyPoint[], current: AnomalyPoint, lookbackDays: number = 90): AnomalyResult | null {
  if (current.amount < FLOOR_INR) return null;
  const cutoff = Date.now() - lookbackDays * 86_400_000;
  const sameCat = history.filter((p) => p.category === current.category && new Date(p.date).getTime() >= cutoff);
  if (sameCat.length < 4) return null;
  const amounts = sameCat.map((p) => p.amount);
  const { mean, std } = meanStd(amounts);
  if (std === 0) return null;
  const z = (current.amount - mean) / std;
  if (Math.abs(z) < 2.5) return null;
  return {
    category: current.category,
    amount: current.amount,
    z,
    baselineMean: mean,
    baselineStd: std,
    verdict: z > 0 ? 'high' : 'low',
  };
}

export function detectWeeklyDrift(history: AnomalyPoint[]): Array<{ category: string; thisWeek: number; baseline: number; pct: number }> {
  const now = Date.now();
  const weekAgo = now - 7 * 86_400_000;
  const fourWeeksAgo = now - 28 * 86_400_000;
  const byCat = new Map<string, { recent: number[]; baseline: number[] }>();
  for (const p of history) {
    const t = new Date(p.date).getTime();
    if (!byCat.has(p.category)) byCat.set(p.category, { recent: [], baseline: [] });
    const slot = byCat.get(p.category);
    if (slot === undefined) continue;
    if (t >= weekAgo) slot.recent.push(p.amount);
    else if (t >= fourWeeksAgo) slot.baseline.push(p.amount / 3);
  }
  const out: Array<{ category: string; thisWeek: number; baseline: number; pct: number }> = [];
  for (const [cat, { recent, baseline }] of byCat) {
    const r = recent.reduce((a, b) => a + b, 0);
    const b = baseline.reduce((a, b2) => a + b2, 0);
    if (b === 0 || r === 0) continue;
    const pct = ((r - b) / b) * 100;
    if (Math.abs(pct) >= 50) {
      out.push({ category: cat, thisWeek: r, baseline: b, pct });
    }
  }
  return out.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
}
